import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/gatsby'
import { datadogRum } from '@datadog/browser-rum-slim'
import { MasterDataLogger } from 'src/errors/logger'

const getReloads = () => {
  if (typeof window === 'undefined') return 0

  return Number(window.sessionStorage.getItem('store:reloads') ?? '0')
}

const setReloads = (reloads: number) => {
  if (typeof window === 'undefined') return

  window.sessionStorage.setItem('store:reloads', `${reloads}`)
}

const canRecover = () => getReloads() < 2

const isFrameworkLevelError = (error: Error) => error?.name === 'ChunkLoadError'

if (typeof window !== 'undefined') {
  window.setInterval(() => setReloads(0), 30e3)
}

class ErrorBoundary extends Component<{ children: ReactNode }> {
  public state = { hasError: false, error: null }

  public static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error,
    }
  }

  // We can't accurately type the error here, since it could vary depending on
  // what caused it. This is not standardized.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async componentDidCatch(error: any, errorInfo: ErrorInfo) {
    console.error(`React Error: ${error.message} ${errorInfo.componentStack}`)

    const errorId = 'unknown'

    // prevent infinite loop
    if (
      window.location.pathname.startsWith('/404') ||
      window.location.pathname.startsWith('/500')
    ) {
      return
    }

    const from = encodeURIComponent(window.location.pathname)

    const isOffline = !window.navigator.onLine
    const is404 = error?.extensions?.exception?.status === 404

    const logger = new MasterDataLogger()

    await logger.logRequest({
      context: 'ErrorBoundary',
      error: `React Error: ${error.message} ${errorInfo.componentStack}`,
      slug: window.location.pathname,
    })

    if (isFrameworkLevelError(error) && canRecover()) {
      setReloads(getReloads() + 1)
      window.location.reload()
    } else if (isOffline) {
      window.location.href = `/offline?from=${from}`
    } else if (is404) {
      window.location.href = `/404?from=${from}`
    } else {
      const storageUserData = window.sessionStorage.getItem('user-decathlon')

      let sub = 'Not logged'

      if (storageUserData) {
        try {
          const userData = JSON.parse(storageUserData)

          sub = userData?.getUserProfile?.sub || 'Not logged'
        } catch {
          console.error('[Error Boundary] Parse user store failed')
          sub = 'Not logged'
        }
      }

      // `cause` carries the original error so Sentry symbolicates its stack
      // (the `extra.stack` copy is minified); the top-level stack stays fixed.
      const cleanError = new Error(`[Boundary] ${error.message}`, {
        cause: error,
      })

      if (cleanError.stack) {
        cleanError.stack = 'default stack'
      }

      datadogRum.addError(cleanError, {
        source: 'error_boundary',
        route: window.location.pathname,
        user_id: sub,
      })

      Sentry.captureException(cleanError, {
        tags: {
          ssr: 'false',
          error_boundary: 'true',
          redirect_to_500: 'true',
          route: window.location.pathname,
        },
        extra: {
          userId: sub,
          slug: window.location.pathname,
          query: window.location.search,
          message: error?.message ?? 'Unknown error',
          stack: error?.stack ?? 'No stack trace',
          componentStack: errorInfo.componentStack,
        },
      })

      Sentry.flush(8000).finally(
        () => (window.location.href = `/500?from=${from}&errorId=${errorId}`)
      )
    }
  }

  public render() {
    if (this.state.hasError) {
      return null
    }

    return this.props.children
  }
}

export default ErrorBoundary
